import { useEffect, useState } from "react";
import {
  BookOpen,
  Bookmark,
  FileText,
  Gauge,
  GraduationCap,
  Heart,
  HelpCircle,
  MessageSquareHeart,
  Home,
  KanbanSquare,
  KeyRound,
  LayoutDashboard,
  Megaphone,
  Newspaper,
  Plus,
  Rocket,
  Send,
  Settings,
  Share2,
  Trash2,
  UserCheck,
  UserRound,
} from "lucide-react";

import AppSidebar, { type NavSection } from "@/components/AppSidebar";
import { SAVED_FILTERS_CHANGED_EVENT, renameSavedFilter, type SavedFilter } from "@/components/ListingFilters";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";

export default function AdvisorSidebar() {
  const { user } = useAuth();
  const [interestCount, setInterestCount] = useState(0);
  const [newAdvisorCount, setNewAdvisorCount] = useState(0);
  const [savedFilters, setSavedFilters] = useState<SavedFilter[]>([]);

  const loadSaved = () => api.get<SavedFilter[]>("/saved-filters/").then(setSavedFilters).catch(() => {});

  useEffect(() => {
    if (!user) return;
    api.get<{ count: number }>("/interests/unread-count").then((d) => setInterestCount(d.count)).catch(() => {});
    api.get<{ count: number }>("/directory/advisors/new-count").then((d) => setNewAdvisorCount(d.count)).catch(() => {});
    loadSaved();
    window.addEventListener(SAVED_FILTERS_CHANGED_EVENT, loadSaved);
    return () => window.removeEventListener(SAVED_FILTERS_CHANGED_EVENT, loadSaved);
  }, [user]);

  const deleteSavedFilter = (id: string) => {
    api.delete(`/saved-filters/${id}`)
      .then(() => {
        loadSaved();
        window.dispatchEvent(new Event(SAVED_FILTERS_CHANGED_EVENT));
      })
      .catch(() => {});
  };

  const sections: NavSection[] = [
    {
      heading: "Overview",
      items: [
        { label: "Dashboard", to: "/bl-advisor/dashboard", icon: Gauge },
        { label: "Home", to: "/bl-advisor/home", icon: Home },
        { label: "Favorites", module: "favorites", to: "/bl-advisor/favorites", icon: Heart },
        { label: "Upcoming Modules", module: "upcoming_modules", to: "/bl-advisor/upcoming-modules", icon: Rocket },
      ],
    },
    {
      heading: "Manage",
      items: [
        { label: "My Listings", module: "my_listings", to: "/bl-advisor/listings", icon: LayoutDashboard },
        { label: "New Listing", module: "new_listing", to: "/bl-advisor/new-listing", icon: Plus },
        { label: "Deleted Listings", module: "deleted_listings", to: "/bl-advisor/listings/deleted", icon: Trash2 },
        { label: "Interests", module: "interests", to: "/bl-advisor/interests", icon: MessageSquareHeart, badgeCount: interestCount, onClick: () => setInterestCount(0) },
        { label: "Shared Listings", module: "shared_listings", to: "/bl-advisor/shared-listings", icon: Send },
      ],
    },
    {
      heading: "Directory",
      items: [
        { label: "All BL Advisors", module: "advisor_directory", to: "/bl-advisor/advisors", icon: UserCheck, badgeCount: newAdvisorCount, onClick: () => setNewAdvisorCount(0) },
      ],
    },
    {
      heading: "Pipeline",
      items: [
        { label: "My Assignments", module: "assignments", to: "/advisor/assignments", icon: UserCheck },
        { label: "Deal CRM", module: "deal_crm", to: "/bl-advisor/pipeline", icon: KanbanSquare },
      ],
    },
    {
      heading: "Account",
      items: [
        { label: "Referrals", module: "referrals", to: "/bl-advisor/referrals", icon: Share2 },
        // Same single page the buyers get — one Academy, role-targeted per
        // lesson by the super admin.
        { label: "BL Academy", module: "bl_academy", to: "/academy", icon: GraduationCap },
        {
          label: "Resource",
          module: "resources",
          to: "/resources",
          icon: BookOpen,
          children: [
            { label: "Glossary", to: "/resources/glossary", icon: BookOpen },
            { label: "FAQ", to: "/resources/faq", icon: HelpCircle },
            { label: "Latest News & Articles", to: "/resources/news", icon: Newspaper },
            { label: "Press & Media", to: "/resources/press", icon: Megaphone },
            { label: "Mastermind", to: "/resources/mastermind", icon: GraduationCap },
          ],
        },
        {
          label: "Settings",
          to: "/bl-advisor/settings",
          icon: Settings,
          children: [
            { label: "My Profile", to: "/bl-advisor/settings/profile", icon: UserRound },
            { label: "Password & Security", to: "/bl-advisor/settings/security", icon: KeyRound },
            { label: "Documents", to: "/bl-advisor/settings/documents", icon: FileText },
          ],
        },
      ],
    },
  ];

  if (savedFilters.length > 0) {
    sections.splice(1, 0, {
      heading: "Saved Filters",
      items: savedFilters.map((f) => ({
        label: f.name,
        to: `/bl-advisor/home?savedFilter=${f.id}`,
        icon: Bookmark,
        onDelete: () => deleteSavedFilter(f.id),
        onRename: (name: string) => { renameSavedFilter(f.id, name).catch(() => {}); },
      })),
    });
  }

  return <AppSidebar sections={sections} homeUrl="/bl-advisor/dashboard" storageKey="advisorSidebarCollapsed" />;
}
